import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, Check } from "lucide-react";
import { useSeo } from "../lib/seo";
import { useAuth } from "../lib/auth";
import { IMAGES, PHONE, PHONE_HREF } from "../lib/constants";
import { PageHero } from "../components/Common";
import { Reveal, PageFade } from "../components/Motion";
import CriteriaForm from "../components/CriteriaForm";

const FOR = [
  "Have capital ready to deploy",
  "Know what type of property they want",
  "Don't have time to constantly search",
  "Want a local acquisition partner",
  "Want access to opportunities they may not discover themselves",
  "Want someone on the ground who can investigate properties",
];

const Thanks = () => {
  const { user } = useAuth();
  return (
    <div className="bg-navy text-paper p-8 sm:p-14" data-testid="criteria-success">
      <CheckCircle2 className="h-10 w-10 text-gold" strokeWidth={1.5} />
      <p className="mt-8 font-serif text-2xl sm:text-3xl leading-snug">Thank you. Your acquisition criteria has been received. A member of Sido Holdings will review your requirements and contact you regarding next steps.</p>
      <div className="mt-10 flex flex-col sm:flex-row gap-3">
        {user && user.role === "investor" && <Link to="/dashboard" className="btn-gold" data-testid="criteria-success-portal">Go to My Investor Portal</Link>}
        <Link to="/opportunities" className="btn-ghost text-paper hover:bg-paper hover:text-navy" data-testid="criteria-success-opps">View Opportunities</Link>
      </div>
    </div>
  );
};

export default function Investors() {
  useSeo("For Investors — Oregon Commercial Real Estate Acquisition", "Submit your commercial real estate investment criteria. Sido Holdings searches Oregon for office, retail, industrial, multifamily, foreclosure and distressed opportunities that fit.");
  const [done, setDone] = useState(false);
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === "#criteria") setTimeout(() => document.getElementById("criteria")?.scrollIntoView({ behavior: "smooth" }), 300);
  }, [hash]);

  return (
    <PageFade>
      <PageHero eyebrow="For Investors" title="Tell Us What You're Looking For. We'll Search Oregon For It." image={IMAGES.concrete}
        sub="Sido Holdings is a local acquisition partner for investors who provide their criteria. We search, investigate, and help move the right opportunities forward." />
      <section className="bg-paper py-20 sm:py-28">
        <div className="container-x grid gap-16 lg:grid-cols-[1fr_1.6fr]">
          <div className="lg:sticky lg:top-28 self-start">
            <p className="eyebrow">Built For Investors Who</p>
            <ul className="mt-8 space-y-4" data-testid="investor-fit-list">
              {FOR.map((x, i) => (
                <Reveal key={x} delay={i * 0.05}>
                  <li className="flex gap-4 border-b border-navy/10 pb-4 text-navy"><Check className="h-5 w-5 text-gold shrink-0 mt-0.5" /><span className="text-lg font-serif">{x}</span></li>
                </Reveal>
              ))}
            </ul>
            <p className="mt-10 text-sm text-mist">Prefer to talk first? <a href={PHONE_HREF} className="text-navy font-semibold underline underline-offset-4" data-testid="investors-phone-link">{PHONE}</a></p>
          </div>
          <div id="criteria" className="scroll-mt-24">
            {done ? <Thanks /> : <CriteriaForm onDone={() => { setDone(true); document.getElementById("criteria")?.scrollIntoView({ behavior: "smooth" }); }} />}
          </div>
        </div>
      </section>
    </PageFade>
  );
}
